import { matchingConfig } from './config'
import { logHeaderScan } from './matchingLog'
import {
  isAbbreviation,
  levenshtein,
  maxFuzzyDistance,
  normalise,
} from './text-similarity'
import { FieldCandidate, MatchResult } from './types'

/**
 * Offline fuzzy column matcher (CRM-1174).
 *
 * Runs only when the AI layer returns null. Each candidate carries a flat list
 * of surface forms — its backend key, its current-language label, every
 * localized title (en/de/fr/pl, from the schema), and the curated synonyms for
 * that field (from field-synonyms). A header is tested against every form of
 * every candidate, short-circuiting on the first tier that hits:
 *   1. exact         — case-insensitive equality with any surface form
 *   2. normalised    — same, after stripping accents and collapsing separators
 *   3. abbreviation  — header is an in-order subsequence of a single-word form
 *                      sharing its first letter (eml→email, grp→group)
 *   4. fuzzy         — Levenshtein distance within a length-relative bound
 * A header that matches two DIFFERENT candidates at the exact/normalised tier is
 * ambiguous and deferred (fieldKey null) — a wrong guess is worse than none.
 * Headers with no hit are omitted.
 */

/**
 * Every comparable surface form of a candidate, deduped: its backend key, its
 * current-language label, and its `aliases` (localized titles + curated
 * synonyms, merged at build time). Testing against all of them is what lets a
 * French "Prénom" or an abbreviated "fname" resolve to `givenName` regardless of
 * the page language — no per-language logic in the matcher.
 */
function surfaceForms(c: FieldCandidate) {
  return [...new Set([c.key, c.label, ...(c.aliases ?? [])])]
}

/** Distinct candidates with any surface form satisfying `predicate`. */
function candidatesMatching(
  candidates: FieldCandidate[],
  predicate: (form: string) => boolean,
): FieldCandidate[] {
  return candidates.filter((c) => surfaceForms(c).some(predicate))
}

function matchOne(
  header: string,
  candidates: FieldCandidate[],
): Omit<MatchResult, 'rawIndex'> | null {
  const rawLower = header.trim().toLowerCase()
  if (!rawLower) {
    return null
  }

  // 1. exact — case-insensitive equality with any surface form
  const exact = candidatesMatching(
    candidates,
    (f) => f.toLowerCase() === rawLower,
  )
  if (exact.length > 1) {
    return null // ambiguous — two fields claim this header; defer to the user
  }
  if (exact.length === 1 && exact[0]) {
    return { fieldKey: exact[0].key, confidence: 'exact' }
  }

  // 2. normalised — accents stripped, separators collapsed, on both sides
  const normHeader = normalise(header)
  const normalised = candidatesMatching(
    candidates,
    (f) => normalise(f) === normHeader,
  )
  if (normalised.length > 1) {
    return null // ambiguous — defer to the user
  }
  if (normalised.length === 1 && normalised[0]) {
    return { fieldKey: normalised[0].key, confidence: 'normalised' }
  }

  // 3. abbreviation — header is a subsequence of a surface form (eml→email).
  // Reported as 'fuzzy' (a review-me guess); the missing score distinguishes it
  // from a Levenshtein hit in the console summary. Only single-word forms are
  // eligible: a multi-word title like "contact since" spuriously contains short
  // subsequences (cat⊆"contact"), so it must not qualify. Among the matches pick
  // the SHORTEST form so `sta` resolves to `status`, not `statusInfo`.
  let abbrev: { fieldKey: string; len: number } | null = null
  for (const c of candidates) {
    for (const form of surfaceForms(c)) {
      const norm = normalise(form)
      if (
        !norm.includes(' ') &&
        isAbbreviation(normHeader, norm, matchingConfig.maxAbbreviationGap) &&
        (!abbrev || norm.length < abbrev.len)
      ) {
        abbrev = { fieldKey: c.key, len: norm.length }
      }
    }
  }
  if (abbrev) {
    return { fieldKey: abbrev.fieldKey, confidence: 'fuzzy' }
  }

  // 3b. token-subset — a qualified header carries a known form as one of its
  // tokens: "company website" → url (via "website"), "consolidated email" →
  // email. Match when the header's normalised tokens are a SUPERSET of some
  // surface form's tokens. Guarded like the exact/normalised tiers: if two
  // different candidates qualify the header is ambiguous, so defer. Single-token
  // forms of 1–2 chars are excluded as subset keys — a header containing a
  // stray "id"/"no" token would over-match.
  const headerWords = normHeader.split(' ')
  const headerTokens = new Set(headerWords)
  const headNoun = headerWords[headerWords.length - 1] // rightmost = head in EN/DE
  const subsetMatches = candidatesMatching(candidates, (form) => {
    const formTokens = normalise(form).split(' ')
    if (formTokens.length >= headerTokens.size) {
      return false // form must be strictly smaller than the header
    }
    if (
      formTokens.length === 1 &&
      (formTokens[0]?.length ?? 0) <= matchingConfig.minSubsetTokenLength
    ) {
      return false // a lone short token is too weak a subset key
    }
    return formTokens.every((tok) => headerTokens.has(tok))
  })
  // A qualified compound like "company website" has two candidates claiming it —
  // `name` via the qualifier "company", `url` via the head "website". Break the
  // tie by the head noun (the header's last token): the field whose form matches
  // it wins. "company website"→url, "company name"→name. Only when exactly one
  // candidate owns the head noun; otherwise it stays ambiguous and defers.
  const resolved =
    subsetMatches.length > 1
      ? candidatesMatching(subsetMatches, (form) =>
          normalise(form)
            .split(' ')
            .includes(headNoun ?? ''),
        )
      : subsetMatches
  if (resolved.length > 1) {
    return null // still ambiguous — defer to the user
  }
  if (resolved.length === 1 && resolved[0]) {
    return { fieldKey: resolved[0].key, confidence: 'fuzzy' }
  }

  // 4. fuzzy — Levenshtein within a length-relative bound, best score wins.
  // The bound is gated by the SHORTER of header and form: a distance-2 match
  // against a 3-char form ("city"→"cat") is meaningless (two of three chars
  // differ) even though the 4-char header alone would permit 2 edits. Using the
  // shorter length kills those short-target collisions while still allowing a
  // real 2-edit typo between two longer strings ("Emial"→"email").
  let best: { fieldKey: string; score: number } | null = null
  for (const c of candidates) {
    for (const form of surfaceForms(c)) {
      const normForm = normalise(form)
      if (
        Math.min(normHeader.length, normForm.length) <
        matchingConfig.fuzzyMinLength
      ) {
        continue // ultra-short form (initialism) — exact/normalised only, no fuzzy
      }
      const distance = levenshtein(normHeader, normForm)
      const bound = maxFuzzyDistance(
        Math.min(normHeader.length, normForm.length),
        matchingConfig.fuzzyShortLength,
        matchingConfig.fuzzyShortMaxDistance,
        matchingConfig.fuzzyLongMaxDistance,
      )
      if (distance <= bound && (!best || distance < best.score)) {
        best = { fieldKey: c.key, score: distance }
      }
    }
  }
  if (best) {
    return { fieldKey: best.fieldKey, confidence: 'fuzzy', score: best.score }
  }

  return null
}

/**
 * Global rank of a match, lower = stronger. Orders the tiers so the strongest
 * column wins a contested field: exact beats normalised beats a dictionary-based
 * fuzzy hit (abbreviation / token-subset, which carry no score) beats a
 * Levenshtein hit (scored, weakest — and the more edits, the weaker).
 */
function strength(m: Omit<MatchResult, 'rawIndex'>) {
  switch (m.confidence) {
    case 'exact':
      return 0
    case 'normalised':
      return 1
    case 'fuzzy':
      return m.score === undefined ? 2 : 3 + m.score
    default:
      return 100
  }
}

// Column→field matching is a 1:1 assignment problem: no two columns may map to
// the same CRM field, and a strong match must not be stolen by a weak one. Per
// column, matchOne already returns its single best target; this resolver adds the
// global constraint on top. Each round every still-unassigned column proposes its
// best match against the fields not yet claimed; a contested field goes to the
// strongest proposer, and the losers stay unassigned to re-propose next round —
// now with that field removed, so they fall through to their next-best target
// (two "email" columns → email + email2). Converges when a round claims nothing
// new; bounded by the field count, so it always terminates. Cheap: a handful of
// columns over a few rounds of pure string math.
export function matchColumnsFuzzy(
  headers: string[],
  fieldCandidates: FieldCandidate[],
): MatchResult[] {
  const assigned = new Map<number, Omit<MatchResult, 'rawIndex'>>()
  const claimed = new Set<string>()
  const columns = headers
    .map((header, rawIndex) => ({ header, rawIndex }))
    .filter((c) => c.header.trim())

  let progressed = true
  while (progressed) {
    progressed = false
    const available = fieldCandidates.filter((c) => !claimed.has(c.key))
    // fieldKey → strongest proposing column this round
    const proposals = new Map<
      string,
      { rawIndex: number; match: Omit<MatchResult, 'rawIndex'> }
    >()
    for (const { header, rawIndex } of columns) {
      if (assigned.has(rawIndex)) {
        continue
      }
      const match = matchOne(header, available)
      if (!match?.fieldKey) {
        continue
      }
      const prev = proposals.get(match.fieldKey)
      if (!prev || strength(match) < strength(prev.match)) {
        proposals.set(match.fieldKey, { rawIndex, match })
      }
    }
    for (const [fieldKey, { rawIndex, match }] of proposals) {
      assigned.set(rawIndex, match)
      claimed.add(fieldKey)
      progressed = true
    }
  }

  const results = [...assigned.entries()]
    .map(([rawIndex, match]) => ({ rawIndex, ...match }))
    .sort((a, b) => a.rawIndex - b.rawIndex)
  return results
}

/**
 * The chosen header row plus the offline matches for it. `matches` is reused by
 * the caller (no re-run) and drives the escalation decision.
 */
export interface HeaderScan {
  rowIndex: number
  matches: MatchResult[]
}

// A CSV may carry preamble lines above the real header (a title, an export
// timestamp, a blank). Rather than assume row 0, score the first few rows by how
// many columns each resolves and pick the best — a header row matches many
// candidates, a preamble line matches ~none. Cheap: pure string math over a
// handful of rows (`matchingConfig.maxHeaderScanRows`). Ties → earliest row.
//
// Short-circuits: as soon as a row matches at least `headerConfidentRatio` of
// its own cells it is accepted and scanning stops — a clean row-0 header ends
// the scan on the first pass instead of always running all five. The winning
// row's matches are returned so the caller doesn't re-run the matcher.
export function detectHeaderRow(
  rows: string[][],
  fieldCandidates: FieldCandidate[],
): HeaderScan {
  let best: HeaderScan = { rowIndex: 0, matches: [] }
  let bestScore = -1
  let scanned = 0
  const scanLimit = Math.min(matchingConfig.maxHeaderScanRows, rows.length)
  for (let i = 0; i < scanLimit; i++) {
    const row = rows[i] ?? []
    if (row.length < 2) {
      continue // a single-cell line is preamble, never a header
    }
    scanned++
    const matches = matchColumnsFuzzy(row, fieldCandidates)
    if (matches.length > bestScore) {
      bestScore = matches.length
      best = { rowIndex: i, matches }
    }
    if (matches.length >= row.length * matchingConfig.headerConfidentRatio) {
      logHeaderScan({
        scanned,
        scanLimit,
        chosenRow: i,
        matched: matches.length,
        columns: row.length,
        shortCircuited: true,
      })
      return best
    }
  }
  logHeaderScan({
    scanned,
    scanLimit,
    chosenRow: best.rowIndex,
    matched: bestScore < 0 ? 0 : bestScore,
    columns: rows[best.rowIndex]?.length ?? 0,
    shortCircuited: false,
  })
  return best
}

/**
 * Merge an offline result with an AI result when both parsed the file into the
 * same columns (the caller checks that before calling). Offline matches win and
 * keep their provenance ("Match" chip); the AI only fills columns offline left
 * unmatched, and never onto a fieldKey offline already claimed (no two columns
 * mapping to one field). This is what lets the user see both technologies at
 * once on an escalated file.
 */
export function mergeMatches(
  offline: MatchResult[],
  ai: MatchResult[],
): MatchResult[] {
  const coveredColumns = new Set(
    offline.filter((m) => m.fieldKey).map((m) => m.rawIndex),
  )
  const claimedKeys = new Set(
    offline.filter((m) => m.fieldKey).map((m) => m.fieldKey),
  )
  const fill = ai.filter(
    (m) =>
      m.fieldKey &&
      !coveredColumns.has(m.rawIndex) &&
      !claimedKeys.has(m.fieldKey),
  )
  return [...offline, ...fill]
}

/**
 * True when the offline result is too weak to trust and the AI should take over.
 * The decisive signal is an unmatched IDENTITY field — a mandatory, non-enum
 * attribute (`familyName` for Person, `name` for Company; the mandatory enum
 * `group` is CRM-assigned and never in a source CSV, so it is excluded by the
 * caller). If the identity field didn't resolve, the parse is likely wrong
 * (mis-detected delimiter, preamble mistaken for the header) or the file is
 * genuinely hard — both are the AI's job. Extra unmatched columns with no CRM
 * target (event/vendor cruft) are NOT a reason to escalate; the AI can't map
 * those either.
 */
export function shouldEscalateToAi(
  matches: MatchResult[],
  identityFieldKeys: string[],
): boolean {
  if (
    !matchingConfig.escalateOnUnmatchedIdentityField ||
    identityFieldKeys.length === 0
  ) {
    return false
  }
  const matchedKeys = new Set(
    matches.filter((m) => m.fieldKey).map((m) => m.fieldKey),
  )
  return identityFieldKeys.some((key) => !matchedKeys.has(key))
}
